// useBillingLines — a building's own billing lines, in the client's words
// (item 867), with what each line actually billed month by month.
//
// The line is facility_billing_lines: the hospital's department name stored
// word for word (TCSD-0253), and TCS's kind of work, role, shifts and basis
// beside it. What a line BILLED comes from the invoice lines stamped to it,
// read through v_client_billing_lines_counted, which counts each month from one
// source only (Ben 2026-10-14). Those rows are Finance's: everyone else reads
// zero of them, so the card asks hasFinanceCapability() whether to show the
// billed columns at all rather than showing an empty month as "nothing billed".
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useReader } from '../../../shared/save-path';

export type BillingBasis = 'Hourly' | 'Monthly_Flat' | 'Per_Day' | 'Per_Event';

export interface BillingLine {
  facility_billing_line_id: string;
  facility_id: string;
  client_label: string;
  service_line_code: string | null;
  service_line_label: string | null;
  role_code: string | null;
  role_label: string | null;
  shift_scope: string | null;
  billing_basis: BillingBasis | null;
  billing_rate: number | null;
  provider_id: string | null;
  provider_name: string | null;
  sub_rate: number | null;
  authorized_hours_per_week: number | null;
  status: 'Active' | 'Paused' | 'Ended';
  effective_start: string | null;
  effective_end: string | null;
  notes: string | null;
}

/** One stamped invoice month, summed per line. */
export interface LineMonth {
  hours: number | null;
  billed: number;
}

export interface BillingMonth {
  /** ISO first-of-month. */
  month: string;
  byLine: Map<string, LineMonth>;
  /** Invoice lines this month not yet on any line — the worklist, not a defect. */
  unplacedCount: number;
  unplacedAmount: number;
}

export interface CodeOption {
  code: string;
  label: string;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** The management fee is Finance's alone and never sits on an account card. */
export function isManagementFeeLabel(label: string): boolean {
  return /management fee/i.test(label);
}

export function useBillingLines(facilityId: string, readBilled: boolean) {
  const [lines, setLines] = useState<BillingLine[]>([]);
  const [months, setMonths] = useState<BillingMonth[]>([]);
  const [serviceLines, setServiceLines] = useState<CodeOption[]>([]);
  const [roles, setRoles] = useState<CodeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useReader(['facility_billing_lines', 'client_billing_lines', 'service_providers', 'rate_change_proposals'], refresh);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [lineRes, slRes, roleRes, billedRes] = await Promise.all([
        supabase
          .from('facility_billing_lines')
          .select(
            'facility_billing_line_id, facility_id, client_label, service_line_code, role_code, shift_scope, billing_basis, billing_rate, provider_id, sub_rate, authorized_hours_per_week, status, effective_start, effective_end, notes, service_lines(label), worker_role_codes(label), service_providers(partner_name)',
          )
          .eq('facility_id', facilityId)
          .order('status')
          .order('client_label'),
        supabase.from('service_lines').select('service_line_code, label, sort_order').eq('is_active', true).order('sort_order'),
        supabase.from('worker_role_codes').select('role_code, label').order('label'),
        readBilled
          ? supabase
              .from('v_client_billing_lines_counted')
              .select('counted_month, facility_billing_line_id, hours, line_amount, is_management_fee')
              .eq('facility_id', facilityId)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (cancelled) return;
      // ⛔ A refused read is not an empty list (PLAYBOOK §15.3). Say which one failed.
      const firstError = lineRes.error ?? slRes.error ?? roleRes.error ?? billedRes.error;
      if (firstError) {
        setError(`The billing lines could not be read: ${firstError.message}`);
        setLoading(false);
        return;
      }
      setError(null);
      setLines(
        ((lineRes.data ?? []) as any[]).map((r) => ({
          facility_billing_line_id: r.facility_billing_line_id,
          facility_id: r.facility_id,
          client_label: r.client_label,
          service_line_code: r.service_line_code,
          service_line_label: r.service_lines?.label ?? null,
          role_code: r.role_code,
          role_label: r.worker_role_codes?.label ?? null,
          shift_scope: r.shift_scope,
          billing_basis: r.billing_basis,
          billing_rate: num(r.billing_rate),
          provider_id: r.provider_id,
          provider_name: r.service_providers?.partner_name ?? null,
          sub_rate: num(r.sub_rate),
          authorized_hours_per_week: num(r.authorized_hours_per_week),
          status: r.status,
          effective_start: r.effective_start,
          effective_end: r.effective_end,
          notes: r.notes,
        })),
      );
      setServiceLines(((slRes.data ?? []) as any[]).map((r) => ({ code: r.service_line_code, label: r.label })));
      setRoles(((roleRes.data ?? []) as any[]).map((r) => ({ code: r.role_code, label: r.label })));

      const byMonth = new Map<string, BillingMonth>();
      for (const r of (billedRes.data ?? []) as any[]) {
        if (!r.counted_month || r.is_management_fee) continue;
        let m = byMonth.get(r.counted_month);
        if (!m) {
          m = { month: r.counted_month, byLine: new Map(), unplacedCount: 0, unplacedAmount: 0 };
          byMonth.set(r.counted_month, m);
        }
        const amount = num(r.line_amount) ?? 0;
        if (!r.facility_billing_line_id) {
          m.unplacedCount += 1;
          m.unplacedAmount += amount;
          continue;
        }
        const cur = m.byLine.get(r.facility_billing_line_id) ?? { hours: null, billed: 0 };
        const h = num(r.hours);
        cur.hours = h === null ? cur.hours : (cur.hours ?? 0) + h;
        cur.billed += amount;
        m.byLine.set(r.facility_billing_line_id, cur);
      }
      setMonths([...byMonth.values()].sort((a, b) => (a.month < b.month ? 1 : -1)));
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [facilityId, readBilled, tick]);

  return { lines, months, serviceLines, roles, loading, error, refresh };
}
